import { parseCollis, computeDifference } from "converterAlgorithm";
import { showToast, hideToast } from "converterUtils";

class ConversionCalculator {
  constructor(workerUrl) {
    this.worker = new Worker(workerUrl);
    this.workerPromises = new Map();
    this.setupWorker();
  }

  setupWorker() {
    this.worker.onmessage = (event) => {
      const { requestId, ...result } = event.data;
      const resolve = this.workerPromises.get(requestId);
      if (resolve) {
        resolve(result);
        this.workerPromises.delete(requestId);
      }
    };

    this.worker.onerror = (error) => {
      console.error("Worker error:", error);
    };
  }

  async calculateProductDifferences(currentId, upgradeId, selectedCollection, partProducts) {
    const currentProduct = this.findProductById(currentId, selectedCollection.products);
    const upgradeProduct = this.findProductById(upgradeId, selectedCollection.products);

    const currentCollis = parseCollis(currentProduct.collis);
    const upgradeCollis = parseCollis(upgradeProduct.collis);
    const difference = computeDifference(currentCollis, upgradeCollis);

    const adjustedCurrentCollis = { ...currentCollis };
    Object.entries(difference.extra).forEach(([sku, qty]) => {
      if (adjustedCurrentCollis[sku]) {
        adjustedCurrentCollis[sku] -= qty;
        if (adjustedCurrentCollis[sku] <= 0) delete adjustedCurrentCollis[sku];
      }
    });

    const allProducts = this.prepareProducts([...selectedCollection.products, ...partProducts]);
    return this.computeResults(difference, allProducts, currentCollis, adjustedCurrentCollis, selectedCollection.products, upgradeId);
  }

  async computeResults(difference, allProducts, currentCollis, adjustedCurrentCollis, products, upgradeId) {
    const toastId = showToast(window.theme.converterStrings.toast.calculating, { type: "info" });

    const calculate = (diff, products, currentCollis) => {
      return new Promise((resolve) => {
        const requestId = `${Date.now()}-${Math.random()}`;
        this.workerPromises.set(requestId, resolve);
        this.worker.postMessage({ diff, products, currentProductCollis: currentCollis, requestId });
      });
    };

    try {
      const results = await Promise.all([
        calculate(difference.diff, allProducts, currentCollis).then((matchingSellableProducts) => ({
          matchingSellableProducts,
        })),
        calculate(adjustedCurrentCollis, allProducts).then((matchingCurrentProducts) => ({
          matchingCurrentProducts,
        })),
        calculate(difference.extra, allProducts).then((matchingExcessProducts) => ({
          matchingExcessProducts,
        })),
      ]);

      const result = Object.assign({}, ...results);
      hideToast(toastId);

      if (this.handleNoMatchingSellableProducts(result, products, upgradeId)) {
        // Toast already shown in handleNoMatchingSellableProducts
      } else {
        showToast(window.theme.converterStrings.toast.finished, { duration: 2000, type: "success" });
      }

      return result;
    } catch (error) {
      hideToast(toastId);
      showToast(window.theme.converterStrings.toast.failed, { duration: 3000, type: "warning" });
      throw error;
    }
  }

  findProductById(productId, products) {
    return products.find((p) => p.product.id == productId);
  }

  prepareProducts(products) {
    return products.map((product) => ({
      ...product,
      collis: product.collis || product.product.variants[0].sku.toString(),
    }));
  }

  handleNoMatchingSellableProducts(result, products, upgradeId) {
    if (result.matchingSellableProducts.products.length === 0) {
      const upgradeProduct = products.find((p) => p.product.id == upgradeId);
      const mappedProduct = {
        id: upgradeProduct.product.id,
        handle: upgradeProduct.product.handle,
        variantId: upgradeProduct.product.variantId,
        collis: parseCollis(upgradeProduct.collis),
      };

      result.matchingSellableProducts.products.push(mappedProduct);

      showToast(window.theme.converterStrings.toast.noMatch, {
        duration: 10000,
        type: "info",
        dismissable: true,
      });
      return true;
    }
    return false;
  }
}

class FlexaConverter extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeData();
    this.initializeDomElements();
    this.calculator = new ConversionCalculator(this.dataset.workerUrl);
    this.setupEventListeners();
  }

  initializeData() {
    this.collectionsData = this.parseJsonFromSelector("[data-collections]");
    this.partProducts = this.parseJsonFromSelector("[data-parts]");
    this.selectedCollection = null;
  }

  initializeDomElements() {
    this.domElements = {
      collections: this.querySelectorAll("[data-collection]"),
    };
  }

  setupEventListeners() {
    this.preventTriggerDefault();
    this.addCollectionClickListeners();
    this.addProductSelectionListener();
  }

  parseJsonFromSelector(selector) {
    return JSON.parse(this.querySelector(selector).innerText);
  }

  preventTriggerDefault() {
    this.querySelectorAll("gtc-trigger").forEach((trigger) => {
      trigger.addEventListener("click", (event) => event.preventDefault());
    });
  }

  addCollectionClickListeners() {
    this.domElements.collections.forEach((collection) => {
      collection.addEventListener("click", (event) => {
        if (event.target.closest("gtc-trigger")) return;
        const collectionIndex = event.target.closest("[data-collection]").dataset.collection;
        this.handleCollectionSelection(collectionIndex);
      });
    });
  }

  addProductSelectionListener() {
    document.addEventListener("product-selected", (event) => {
      const { role } = event.detail;
      const hasUpgrade = sessionStorage.getItem("selected-upgrade-id");

      if ((role === "current" && hasUpgrade) || role === "upgrade") {
        setTimeout(() => this.computeConversion(), 100);
      }
    });
  }

  handleCollectionSelection(collectionIndex) {
    this.selectedCollection = this.collectionsData[collectionIndex];
    sessionStorage.removeItem("selected-current-id");
    sessionStorage.removeItem("selected-upgrade-id");

    this.dispatchEvent("collection-selected", this.selectedCollection);
  }

  async computeConversion() {
    document.dispatchEvent(new Event("calculating-products"));

    const currentId = sessionStorage.getItem("selected-current-id");
    const upgradeId = sessionStorage.getItem("selected-upgrade-id");

    if (!this.isValidSelection(currentId, upgradeId)) return;

    const calculation = await this.calculator.calculateProductDifferences(
      currentId,
      upgradeId,
      this.selectedCollection,
      this.partProducts
    );
    this.dispatchCalculationResults(calculation);
  }

  isValidSelection(currentId, upgradeId) {
    return currentId && upgradeId;
  }

  dispatchEvent(eventName, detail) {
    document.dispatchEvent(new CustomEvent(eventName, { detail }));
  }

  dispatchCalculationResults(calculation) {
    this.dispatchEvent("converter-calculated", calculation);
  }
}

if (!customElements.get("flexa-converter")) {
  customElements.define("flexa-converter", FlexaConverter);
}